import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

/**
 * The Generation Gateway's moderation decision, passed through by the backend as
 * `moderation` on generate responses (docs: "Moderation decision" in the gateway's
 * integration guide). Only blocking decisions are shown to the user.
 */
export interface ModerationDecision {
  requestId: string;
  decision: "allowed" | "blocked" | "unavailable";
  action: "allow" | "block";
  reasons: { code: string; message: string }[];
}

export interface ModerationPopupContent {
  requestId: string;
  title: string;
  reasons: { code: string; message: string }[];
}

export function isModerationDecision(value: unknown): value is ModerationDecision {
  const v = value as Partial<ModerationDecision> | null;
  return (
    typeof v === "object" && v !== null &&
    typeof v.requestId === "string" &&
    (v.action === "allow" || v.action === "block") &&
    (v.decision === "allowed" || v.decision === "blocked" || v.decision === "unavailable") &&
    Array.isArray(v.reasons) &&
    v.reasons.every((r) => typeof r?.code === "string" && typeof r?.message === "string")
  );
}

function popupContent(result: ModerationDecision): ModerationPopupContent | null {
  if (result.action !== "block") return null;
  return {
    requestId: result.requestId,
    title: result.decision === "unavailable" ? "Unable to check content" : "Content could not be accepted",
    reasons: result.reasons.map((reason) =>
      reason.code === "CONTENT_POLICY"
        ? { ...reason, message: "We block underage content. If you believe this was a mistake, please contact support at support@animepup.com." }
        : reason,
    ),
  };
}

const Context = createContext<{ showModerationResult(result: ModerationDecision): boolean } | null>(null);

/** Queues blocking decisions and shows them one at a time. */
export function ModerationProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<ModerationPopupContent[]>([]);
  const showModerationResult = useCallback((result: ModerationDecision) => {
    const content = popupContent(result);
    if (!content) return false;
    setQueue((items) => (items.some((item) => item.requestId === content.requestId) ? items : [...items, content]));
    return true;
  }, []);
  const close = useCallback(() => setQueue((items) => items.slice(1)), []);
  const context = useMemo(() => ({ showModerationResult }), [showModerationResult]);
  return (
    <Context.Provider value={context}>
      {children}
      {queue[0] && <ModerationPopup key={queue[0].requestId} content={queue[0]} onClose={close} />}
    </Context.Provider>
  );
}

export function useModeration() {
  const context = useContext(Context);
  if (!context) throw new Error("Mount ModerationProvider before using useModeration");
  return context;
}

function ModerationPopup({ content, onClose }: { content: ModerationPopupContent; onClose(): void }) {
  const dialog = useRef<HTMLDialogElement>(null);
  // Focus returns to whatever opened the popup once it closes.
  const opener = useRef<Element | null>(null);
  useEffect(() => {
    opener.current = document.activeElement;
    if (dialog.current && !dialog.current.open) dialog.current.showModal();
    return () => { if (opener.current instanceof HTMLElement && opener.current.isConnected) opener.current.focus(); };
  }, []);
  return (
    <dialog
      ref={dialog}
      aria-labelledby={`moderation-title-${content.requestId}`}
      onClose={onClose}
      style={{ background: "linear-gradient(170deg, #2a2035 0%, #1a1520 50%, #1a1520 100%)" }}
      className="m-auto w-[calc(100%-2rem)] max-w-sm max-h-[calc(100dvh-2rem)] overflow-y-auto rounded-3xl border border-primary/15 p-6 text-center text-foreground shadow-2xl shadow-black/40 backdrop:bg-black/60 backdrop:backdrop-blur-md"
    >
      <h2 id={`moderation-title-${content.requestId}`} className="mb-3 text-xl font-extrabold tracking-tight">{content.title}</h2>
      <ul className="mb-6 list-none space-y-2 p-0 text-[13px] leading-relaxed text-muted-foreground">
        {content.reasons.map((reason, index) => <li key={`${reason.code}-${index}`} className="break-words">{reason.message}</li>)}
      </ul>
      <button type="button" autoFocus onClick={() => dialog.current?.close()} className="btn-primary w-full min-h-11 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring">
        Got it
      </button>
    </dialog>
  );
}
